/**
 * Each hook validates the BE response with the schema matching its actor tier
 * so BE/FE drift surfaces as a loud parse error rather than silent confusion.
 */

import type { QueryClient } from "@tanstack/react-query";
import type { z } from "zod";
import {
  type ConfigureCrmBody,
  type ConfigureCrmResult,
} from "@shared/schemas/configure-crm.schemas";
import {
  anyIdCompanion,
  EventIdPublic,
} from "@shared/schemas/branded-id.schemas";
import {
  EventAdminViewSchema,
  EventOwnerViewSchema,
  EventPublicViewSchema,
  EventUserViewSchema,
  EventUpdateableInternalSchema,
  type EventOwnerView,
  type EventAdminView,
} from "@shared/schemas/event.schemas";
import type {
  CreateEventRequest,
  EventBrandingField,
  EventBrandingPendingReference,
  UpdateEventRequest,
} from "@shared/schemas/event-branding-upload.schemas";
import { BaseApiHooksRepository } from "../../base-api-hooks.repository";
import type {
  GetEventAdminViewOptions,
  GetEventOptions,
  GetEventOwnerViewOptions,
  GetEventPublicViewOptions,
  GetEventUserViewOptions,
} from "@frontend/shared/features/events/domain/types/event.types";
import { createEventsApi } from "./events.api";
import type { AxiosInstance } from "axios";
import { EVENTS_QUERY_KEYS } from "./events-query-keys.constants";
import { onEventCreated, onEventUpdated } from "../../cache-actions";
import { EventJoinInfoSchema } from "@shared/schemas/event-join-info.schemas";
import {
  EventViewUnionSchema,
  MyEventViewUnionSchema,
} from "@shared/schemas/event-views.schemas";
import type {
  EventViewUnion,
  MyEventViewUnion,
} from "@shared/schemas/event-views.schemas";

type EventUpdateableInternalInput = z.input<
  typeof EventUpdateableInternalSchema
>;

interface GetEventsListOptions {
  readonly enabled?: boolean | undefined;
}

export class EventHooksHttpRepositoryImpl extends BaseApiHooksRepository {
  private readonly api;

  constructor(queryClient: QueryClient, apiClient: AxiosInstance) {
    super(queryClient);
    this.api = createEventsApi(apiClient);
  }

  createHooks() {
    return {
      useGetEvent: (options: GetEventOptions) => {
        const eventIdPublicOrSlug = anyIdCompanion.unwrap(
          options.eventIdPublicOrSlug,
        );
        return this.query({
          key: EVENTS_QUERY_KEYS.byIdPublicOrSlug(eventIdPublicOrSlug),
          fn: () => this.api.getByIdPublicOrSlug(eventIdPublicOrSlug),
          // The by-id endpoint returns the standard role-tier views; list-only
          // participant counts are parsed by useGetMyEvents below.
          parse: (dto): EventViewUnion => EventViewUnionSchema.parse(dto),
          enabled: (options.enabled ?? true) && eventIdPublicOrSlug.length > 0,
          staleTime: 5 * 60 * 1000,
        });
      },

      useGetEventUserView: (options: GetEventUserViewOptions) => {
        const eventIdPublicOrSlug = anyIdCompanion.unwrap(
          options.eventIdPublicOrSlug,
        );
        return this.query({
          key: EVENTS_QUERY_KEYS.byIdPublicOrSlug(eventIdPublicOrSlug),
          fn: () => this.api.getByIdPublicOrSlug(eventIdPublicOrSlug),
          parse: (dto) => EventUserViewSchema.parse(dto),
          enabled: (options.enabled ?? true) && eventIdPublicOrSlug.length > 0,
          staleTime: 5 * 60 * 1000,
        });
      },

      useGetEventOwnerView: (options: GetEventOwnerViewOptions) => {
        const eventIdPublicOrSlug = anyIdCompanion.unwrap(
          options.eventIdPublicOrSlug,
        );
        return this.query({
          key: EVENTS_QUERY_KEYS.byIdPublicOrSlug(eventIdPublicOrSlug),
          fn: () => this.api.getByIdPublicOrSlug(eventIdPublicOrSlug),
          parse: (dto) => EventOwnerViewSchema.parse(dto),
          enabled: (options.enabled ?? true) && eventIdPublicOrSlug.length > 0,
          staleTime: 5 * 60 * 1000,
        });
      },

      useGetEventAdminView: (options: GetEventAdminViewOptions) => {
        const eventIdPublicOrSlug = anyIdCompanion.unwrap(
          options.eventIdPublicOrSlug,
        );
        return this.query({
          key: EVENTS_QUERY_KEYS.byIdPublicOrSlugAdmin(eventIdPublicOrSlug),
          fn: () => this.api.getByIdPublicOrSlug(eventIdPublicOrSlug),
          parse: (dto) => EventAdminViewSchema.parse(dto),
          enabled: (options.enabled ?? true) && eventIdPublicOrSlug.length > 0,
          staleTime: 5 * 60 * 1000,
        });
      },

      useGetEventPublicView: (options: GetEventPublicViewOptions) => {
        const eventIdPublicOrSlug = anyIdCompanion.unwrap(
          options.eventIdPublicOrSlug,
        );
        return this.query({
          key: EVENTS_QUERY_KEYS.publicInfo(eventIdPublicOrSlug),
          fn: () => this.api.getPublicView(eventIdPublicOrSlug),
          parse: (dto) => EventPublicViewSchema.parse(dto),
          enabled: (options.enabled ?? true) && eventIdPublicOrSlug.length > 0,
          staleTime: 5 * 60 * 1000,
          retry: 1,
        });
      },

      useGetEventJoinInfo: (
        eventIdPublicOrSlug: string,
        options: {
          readonly inv?: string | undefined;
          readonly lang?: string | undefined;
        } = {},
      ) =>
        this.query({
          key: EVENTS_QUERY_KEYS.joinInfo(
            eventIdPublicOrSlug,
            options.inv,
            options.lang,
          ),
          fn: () => this.api.getEventJoinInfo(eventIdPublicOrSlug, options),
          parse: (dto) => EventJoinInfoSchema.parse(dto),
          enabled: eventIdPublicOrSlug.length > 0,
          staleTime: 5 * 60 * 1000,
          retry: 1,
        }),

      useGetMyEvents: (options: GetEventsListOptions = {}) => {
        const { enabled = true } = options;

        return this.query<readonly unknown[], readonly MyEventViewUnion[]>({
          key: EVENTS_QUERY_KEYS.all,
          fn: () => this.api.getMyEvents(),
          parse: (dtos) => dtos.map((dto) => MyEventViewUnionSchema.parse(dto)),
          enabled,
          staleTime: 30 * 1000,
          retry: 1,
        });
      },

      useGetAllEvents: (options: GetEventsListOptions = {}) => {
        const { enabled = true } = options;

        return this.query<readonly unknown[], readonly EventAdminView[]>({
          key: EVENTS_QUERY_KEYS.allAdmin,
          fn: () => this.api.getAllEvents(),
          parse: (dtos) => dtos.map((dto) => EventAdminViewSchema.parse(dto)),
          enabled,
          staleTime: 30 * 1000,
          retry: 1,
        });
      },

      useCreateEvent: () =>
        this.mutation<unknown, EventOwnerView, CreateEventRequest>({
          fn: (domainData) => this.api.create(domainData),
          parse: (dto) => EventOwnerViewSchema.parse(dto),
          onSuccess: (createdEvent) => {
            onEventCreated(this.queryClient);
            this.setCachedData(
              EVENTS_QUERY_KEYS.byIdPublicOrSlug(
                EventIdPublic.unwrap(createdEvent.idPublic),
              ),
              createdEvent,
            );
          },
        }),

      useStageEventBrandingUpload: () =>
        this.mutation<
          EventBrandingPendingReference,
          EventBrandingPendingReference,
          {
            readonly eventIdPublic?: string | undefined;
            readonly field: EventBrandingField;
            readonly file: File;
          }
        >({
          fn: (input) => this.api.stageBrandingUpload(input),
          parse: (result) => result,
        }),

      useUpdateEventHost: () =>
        this.mutation<
          unknown,
          EventOwnerView,
          {
            readonly idPublic: string;
            readonly data: UpdateEventRequest;
          }
        >({
          fn: ({ idPublic, data }) => this.api.update(idPublic, data),
          parse: (dto) => EventOwnerViewSchema.parse(dto),
          onSuccess: (updatedEvent) => {
            onEventUpdated(this.queryClient, updatedEvent.idPublic);
          },
        }),

      useUpdateEventInternal: () =>
        this.mutation<
          unknown,
          EventAdminView,
          {
            readonly idPublic: string;
            readonly data: EventUpdateableInternalInput;
          }
        >({
          fn: ({ idPublic, data }) => this.api.updateInternal(idPublic, data),
          parse: (dto) => EventAdminViewSchema.parse(dto),
          onSuccess: (updatedEvent) => {
            onEventUpdated(this.queryClient, updatedEvent.idPublic);
          },
        }),

      useConfigureCrm: () =>
        this.mutation<
          ConfigureCrmResult,
          ConfigureCrmResult,
          {
            readonly idPublic: string;
            readonly data: ConfigureCrmBody;
          }
        >({
          fn: ({ idPublic, data }) => this.api.configureCrm(idPublic, data),
          parse: (dto) => dto,
          onSuccess: (_response, variables) => {
            onEventUpdated(
              this.queryClient,
              EventIdPublic.unsafeCreate(variables.idPublic),
            );
          },
        }),
    };
  }
}
